<script setup>
import { Link } from "@inertiajs/vue3";

const {
  title,
  icon = null,
  href,
  callback,
  active = false,
  level = 0,
  type = "default",
  disclosure,
  setCurrentDisclosure,
  pinValue = undefined,
  pingType = "warning",
  animatePing = false,
  hideTextOnMobile = false,
} = defineProps({
  title: {
    type: String,
    required: true,
  },
  icon: {
    type: [Object, Function],
    required: false,
  },
  href: {
    type: String,
    required: false,
  },
  callback: {
    type: Function,
    required: false,
  },
  active: {
    type: Boolean,
    required: false,
  },
  level: {
    type: Number,
    required: false,
  },
  type: {
    type: String,
    required: false,
  },
  disclosure: {
    required: false,
  },
  setCurrentDisclosure: {
    type: Function,
    required: false,
  },
  pinValue: {
    type: [Number, String],
    required: false,
  },
  pingType: {
    type: String,
    required: false,
  },
  animatePing: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideTextOnMobile: {
    type: Boolean,
    required: false,
    default: false,
  },
});

function toggleButton() {
  if (callback) {
    callback();
  } else if (disclosure && setCurrentDisclosure) {
    setCurrentDisclosure(disclosure);
  }
}
</script>

<template>
  <component
    v-if="href || callback"
    :is="href ? Link : 'button'"
    :type="href ? 'button' : undefined"
    :href="href ?? undefined"
    class="group relative flex flex-shrink-0 items-center gap-2 truncate rounded-lg p-2 duration-150 hover:bg-indigo-600 hover:text-white"
    :class="{
      'bg-indigo-600 text-white': active,
      'text-amber-500 hover:bg-amber-400': type === 'warning',
      'bg-amber-400 !text-white': type === 'warning' && active,
      '!overflow-visible': pinValue !== undefined,
    }"
    @click="toggleButton"
  >
    <component
      v-if="icon && level === 0"
      :is="icon"
      class="inline-block size-5 group-hover:text-white"
      :class="{
        'text-gray-400': type === 'default',
        'text-amber-400 group-hover:text-white': type === 'warning',
        'text-white': active,
        '!text-white': type === 'warning' && active,
      }"
    />

    <div
      :class="{
        'pl-7': level === 1,
        'pl-12': level === 2,
        'max-xl:hidden': hideTextOnMobile,
      }"
    >
      {{ title }}
    </div>

    <div
      v-if="pinValue !== undefined"
      class="absolute -right-1 -top-1 flex size-5"
    >
      <span
        class="absolute inline-flex h-full w-full rounded-full opacity-75"
        :class="{
          'bg-red-300': pingType === 'danger',
          'bg-yellow-300': pingType === 'warning',
          'bg-green-300': pingType === 'success',
          'animate-ping': animatePing,
        }"
      >
      </span>
      <span
        class="relative inline-flex size-5 items-center justify-center rounded-full text-xs text-white"
        :class="{
          'bg-red-500': pingType === 'danger',
          'bg-yellow-500': pingType === 'warning',
          'bg-green-500': pingType === 'success',
        }"
      >
        {{ pinValue }}
      </span>
    </div>
  </component>

  <div
    v-else
    class="group flex flex-shrink-0 cursor-not-allowed items-center gap-2 truncate rounded-lg p-2 opacity-50 duration-150"
    :class="{
      'bg-indigo-600 text-white': active,
      'text-amber-500 hover:bg-amber-400': type === 'warning',
      'bg-amber-400 !text-white': type === 'warning' && active,
    }"
  >
    <component
      v-if="icon && level === 0"
      :is="icon"
      class="inline-block size-5 group-hover:text-white"
      :class="{
        'text-gray-400': type === 'default',
        'text-amber-400 group-hover:text-white': type === 'warning',
        'text-white': active,
        '!text-white': type === 'warning' && active,
      }"
    />

    <div :class="{ 'pl-7': level === 1, 'pl-12': level === 2 }">
      {{ title }}
    </div>
  </div>
</template>
